<template>
	<div
		class="actionbar card"
		:exts-open="resourceExtsOpen"
		:history-scrolled-to-bottom="historyScrolledToBottom"
		:style="
			resourceExtsOpen
				? 'height: 100%; '
				: inputExtsOpen
				? 'height: auto; '
				: 'height: ' + actionbarInnerHeight + 'px; '
		"
	>
		<transition name="exts-open">
			<div
				:class="
					'exts-container ' +
						(resourceExtsOpen || inputExtsOpen ? 'hardshadow' : '')
				"
				:style="
					resourceExtsOpen
						? 'height: calc(100% - ' +
						  actionbarInnerHeight +
						  'px); '
						: inputExtsOpen
						? 'height: auto; '
						: 'height: 0; '
				"
			>
				<ResourceExtUI
					v-show="resourceExtsOpen"
					@update:addSnippet="updateAddSnippet($event)"
				/>
				<InputExtUI
					v-show="inputExtsOpen"
					:msgBody="msgBodyC"
					:selectionStart="selectionStart"
					:selectionEnd="selectionEnd"
					:selection="selection"
					@update:msgBody="updateMsgBody($event)"
					@update:addSnippet="updateAddSnippet($event)"
					@update:active="updateInputExtsOpen($event)"
					class="shadow"
				/>
			</div>
		</transition>
		<div
			class="actionbar-inner input-group"
			ref="actionbarInner"
			:style="'height: ' + actionbarInnerHeight + 'px; '"
		>
			<button
				@click="toggleResourceExts()"
				class="toggle-resource-exts-btn btn action lg shadow secondary"
			>
				<icon>dots-vertical</icon>
			</button>
			<textarea
				v-model="msgBody"
				@keypress.enter.exact.prevent="send"
				:rows="rows"
				:max-rows="maxRows"
				class="msginput input"
				ref="msginput"
				placeholder="Write a message"
			></textarea>
			<button
				@click="send()"
				class="sendbtn btn action lg shadow primary"
				v-bind="sendBtnBinds"
			>
				<icon>send</icon>
			</button>
			<span
				ref="msgBodyHeightTest"
				style="visibility: hidden; position: absolute; pointer-events: none; width: calc(100% - 6.4rem - .8rem + 2px); left: 3.2rem; overflow: hidden;"
				class="msginput input"
			></span>
		</div>
	</div>
</template>

<script>
import Icon from './Icon';
import ResourceExtUI from './ResourceExtUI';
import InputExtUI from './InputExtUI';
import { mapGetters, mapActions } from 'vuex';

function clamp(val, min, max) {
	return val > max ? max : val < min ? min : val;
}

let ticking = false;

const ticker = dis => {
	if (ticking) {
		window.requestAnimationFrame(() => {
			dis.selectionStart =
				dis.$refs.msginput.selectionStart >= 0
					? dis.$refs.msginput.selectionStart
					: dis.msgBody.length;
			dis.selectionEnd =
				dis.$refs.msginput.selectionEnd >= 0
					? dis.$refs.msginput.selectionEnd
					: dis.msgBody.length;

			setTimeout(() => {
				ticker(dis);
			}, 400);
		});
	}
};

let typingTimeout = null;

export default {
	name: 'Actionbar',
	components: {
		Icon,
		ResourceExtUI,
		InputExtUI
	},
	props: ['historyScrolledToBottom'],
	data() {
		return {
			selectionStart: 0,
			selectionEnd: 0,
			msgBody: '',
			rows: 1,
			maxRows: 10,
			resourceExtsOpen: false,
			inputExtsOpen: false,
			actionbarInnerHeight: 0
		};
	},
	mounted() {
		this.$nextTick(() => {
			this.selectionStart =
				this.$refs.msginput.selectionStart >= 0
					? this.$refs.msginput.selectionStart
					: this.msgBody.length;
			this.selectionEnd =
				this.$refs.msginput.selectionEnd >= 0
					? this.$refs.msginput.selectionEnd
					: this.msgBody.length;
			this.$refs.msginput.addEventListener('focus', e => {
				if (!ticking) {
					ticking = true;
					ticker(this);
				}
			});
			this.$refs.msginput.addEventListener('blur', e => {
				this.selectionStart =
					e.target.selectionStart >= 0
						? e.target.selectionStart
						: this.msgBody.length;
				this.selectionEnd =
					e.target.selectionEnd >= 0
						? e.target.selectionEnd
						: this.msgBody.length;
				ticking = false;
			});
		});
	},
	computed: {
		msgBodyC() {
			return this.msgBody;
		},
		selection() {
			let selectionStart = this.selectionStart;
			let selectionEnd = this.selectionEnd;

			if (selectionStart > selectionEnd) {
				let oldSelectionEnd = selectionEnd;
				selectionEnd = selectionStart;
				selectionStart = oldSelectionEnd;
			}

			return this.msgBody.substring(selectionStart, selectionEnd);
		},
		sendBtnBinds() {
			let binds = {};
			if (!this.validateMessage(this.msgBody)) {
				binds.disabled = 'true';
			}
			return binds;
		},
		...mapGetters({
			validateMessage: 'ConversationProviders/validateMessage'
		})
	},
	methods: {
		updateMsgBody(nMsgBody) {
			this.msgBody = nMsgBody;
		},
		updateAddSnippet(snippet) {
			let selectionStart = this.selectionStart;
			let selectionEnd = this.selectionEnd;

			if (selectionStart > selectionEnd) {
				let oldSelectionEnd = selectionEnd;
				selectionEnd = selectionStart;
				selectionStart = oldSelectionEnd;
			}

			let selectedStart = this.msgBody.substring(0, selectionStart);
			let selectedEnd = this.msgBody.substring(
				selectionEnd,
				this.msgBody.length
			);

			this.msgBody = selectedStart + snippet + selectedEnd;

			this.$refs.msginput.selectionStart = selectedStart + snippet.length;
			this.$refs.msginput.selectionEnd = selectedStart + snippet.length;
			this.selectionStart = selectedStart + snippet.length;
			this.selectionEnd = selectedStart + snippet.length;
		},
		updateInputExtsOpen(to = false) {
			this.inputExtsOpen = to;
		},
		send() {
			this.sendMessage2Cur(this.msgBody)
				.then(messageData => {
					if (messageData) {
						this.$nextTick(() => {
							this.msgBody = '';
							this.updateTyping(false);
						});
					}
				})
				.catch(err => {
					console.error(err);
				});
		},
		toggleResourceExts(to = !this.resourceExtsOpen) {
			this.resourceExtsOpen = !!to;
		},
		toggleInputExts(to = !this.inputExtsOpen) {
			this.inputExtsOpen = !!to;
		},
		setActionbarInnerHeight() {
			let rows = this.msgBody.split('\n').length;
			this.rows = clamp(rows, 1, this.maxRows);

			let preparedMsgBody = document.createElement('span');
			let preparedMsgBodyArr = this.msgBody.split('\n');
			for (let i = 0; i < preparedMsgBodyArr.length; i++) {
				let p = preparedMsgBodyArr[i];
				let c = document.createElement('span');
				c.innerText = p;
				preparedMsgBody.appendChild(c);
				preparedMsgBody.appendChild(document.createElement('br'));
			}
			this.$refs.msgBodyHeightTest.innerHTML = '';
			this.$refs.msgBodyHeightTest.appendChild(preparedMsgBody);

			// this.$refs.msgBodyHeightTest.innerHTML = this.msgBody.replace(/\n/gm, '<br />');
			this.$nextTick(() => {
				let msgBodyHeight = this.$refs.msgBodyHeightTest.offsetHeight;
				this.$refs.msginput.style.height =
					'calc(' + msgBodyHeight + 'px - 2 * .225rem)';
				this.actionbarInnerHeight = msgBodyHeight;
			});
		},
		...mapActions({
			sendMessage2Cur: 'ConversationProviders/sendMessage2Cur',
			updateTyping: 'ConversationProviders/updateTyping'
		})
	},
	watch: {
		msgBody() {
			this.setActionbarInnerHeight();

			this.toggleInputExts(
				this.msgBody.trim() && this.$children[1].anyActiveComponent
			);

			if (this.msgBody.trim()) {
				clearTimeout(typingTimeout);
				this.updateTyping(true);
			}

			typingTimeout = setTimeout(() => {
				this.updateTyping(false);
			}, 2500);
		}
	}
};
</script>
